import { useHtmlPresignRewrite } from "@/hooks/useHtmlPresignRewrite";
import { useCanvasUi } from "@/pages/canvas/context/CanvasUiContext";
import { useHtmlBlockEditSession } from "@/pages/canvas/hooks/useHtmlBlockEditSession";
import {
  useHtmlBlockFocus,
  type HtmlBlockFocusMode,
} from "@/pages/canvas/hooks/useHtmlBlockFocus";
import {
  useThemeContext,
  type BlockEntryPoint,
  type BlockToParentMessage,
  type HtmlIslandAutosaveState,
} from "@cohesive/ui-components";
import { useStoreApi } from "@xyflow/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CanvasBlockPresentation } from "../types/canvasNodeData";

export interface UseHtmlBlockNodeParams {
  nodeId: string;
  /** The node's `data.presentation` — its presence is the block-mode marker. */
  presentation: CanvasBlockPresentation;
  /** File record id backing this block, when hydrated. */
  fileRecordId?: string;
  /** Node still showing its loading skeleton. */
  loading: boolean;
  selected: boolean;
  dragging: boolean;
  canEdit: boolean;
}

export interface HtmlBlockNodeState {
  presentation: CanvasBlockPresentation;
  /** App theme, threaded into the card so the iframe's injected color scheme
   *  matches its embedder (a mismatch paints an opaque backstop and silently
   *  breaks transparent blocks). */
  theme: "light" | "dark";
  /** Swaps embedded public file URLs for presigned ones before render. */
  transformHtml: (html: string) => Promise<string>;
  /** Which mode the user is in on this block, if any. */
  focusMode: HtmlBlockFocusMode | null;
  /**
   * Double-click / Enter routes INTO this block rather than to the pane.
   * True when entering buys something: interaction, or island editing.
   */
  canEnter: boolean;
  /**
   * Enter the block (double-click / Enter key): edit mode when the user can
   * edit and the document has islands (a superset of interaction — buttons
   * outside islands still work), otherwise interact mode. The optional point
   * is the gesture position in iframe content coordinates; an edit entry
   * forwards it so the caret lands under the pointer.
   */
  enter: (point?: BlockEntryPoint) => void;
  /**
   * Toolbar entry ("Make interactive"): enter exactly as a double-click
   * would, but tolerant of the capability arriving one render late — the
   * toolbar click may persist `interactive: true` in the same handler, and
   * the flag only reaches this hook through the node-data re-render. The
   * request arms a one-shot that fires as soon as entry is possible, and
   * disarms on deselection so a never-capable click can't enter later.
   */
  requestEnter: () => void;
  /** Hover pill copy announcing what double-click does on this block. */
  enterHintLabel: string;
  /** The live document reports at least one editable island. */
  hasIslands: boolean;
  /** Hovering the card should show the island glow (pre-entry affordance). */
  islandHint: boolean;
  /** Caret target for the current edit session's entry gesture, if any. */
  editFocusPoint: BlockEntryPoint | null;
  /** Runtime ready handshake — carries the live island count. */
  onRuntimeReady: (info: { islands: number }) => void;
  /** Persisted `presentation.interactive`, for the toolbar toggle's state. */
  interactiveFlag: boolean;
  /**
   * The iframe should receive pointer events. Being inside the block is
   * necessary but not sufficient: an in-flight drag or the hand tool means
   * the canvas is mid-gesture and must keep the pointer.
   */
  iframeOwnsPointer: boolean;
  /** Runtime messages from the block's injected script. */
  onBlockMessage: (msg: BlockToParentMessage) => void;
  /** Leave whatever mode this block is in. */
  exit: () => void;
  /** An edit session is active — islands are contenteditable. */
  islandsEditable: boolean;
  /** Start/stop the edit session (toolbar pencil). */
  toggleEditSession: () => void;
  /** Hold refetches out of the iframe while editing/saving/settling. */
  contentFrozen: boolean;
  /** Pending/saving counts for the grip pill. */
  saveState: HtmlIslandAutosaveState;
}

/**
 * Everything an HTML block node needs, gathered in one place.
 *
 * Mounted ONLY by the block wrapper in EntityNode — i.e. only for nodes that
 * actually carry `data.presentation`. That is why this may freely use hooks
 * that cost something (a presign map, latch subscriptions, and later a
 * react-query mutation): an image or PDF node never mounts them.
 *
 * Gesture model ("embedded object"): double-click and Enter mean "enter the
 * block" everywhere, routed to the most useful mode — edit (canEdit + the
 * document has islands), else interact (presentation.interactive), else the
 * caller falls through to opening the detail pane. The hover pill announces
 * the outcome before the gesture, so routing never hinges on invisible state.
 */
export function useHtmlBlockNode({
  nodeId,
  presentation,
  fileRecordId,
  loading,
  selected,
  dragging,
  canEdit,
}: UseHtmlBlockNodeParams): HtmlBlockNodeState {
  const { mode: themeMode } = useThemeContext();
  const theme = themeMode === "dark" ? "dark" : "light";
  const { cursorTool } = useCanvasUi();
  const storeApi = useStoreApi();

  const presign = useHtmlPresignRewrite({ fileRecordId });

  const interactiveFlag = presentation.interactive === true;
  // Interaction is deliberately not gated on canEdit — viewers of a published
  // canvas can enter an interactive block.
  const canInteract = interactiveFlag && !loading;

  // The live document's island count, reported by the runtime's ready
  // handshake (the frame is opaque-origin, so this is the host's only view).
  const [hasIslands, setHasIslands] = useState(false);
  const onRuntimeReady = useCallback((info: { islands: number }) => {
    setHasIslands(info.islands > 0);
  }, []);

  const canEnterEdit = canEdit && !loading;
  // Editing is only a useful entry target when there is something to edit.
  const canEnterEditIslands = canEnterEdit && hasIslands;

  // `flush` is read through a ref because the session hook needs the focus
  // latch (for `editing`) and the latch needs the session (to flush on its
  // falling edge) — the ref breaks that cycle without deferring the flush.
  const flushRef = useRef<() => void>(() => {});
  const onModeEnd = useCallback((previousMode: HtmlBlockFocusMode) => {
    if (previousMode === "edit") flushRef.current();
  }, []);

  const focus = useHtmlBlockFocus({
    nodeId,
    canInteract,
    canEnterEdit,
    selected,
    dragging,
    onModeEnd,
  });

  const editing = focus.mode === "edit";
  const session = useHtmlBlockEditSession({
    fileRecordId,
    editing,
    reversePresignedUrls: presign.reversePresignedUrls,
  });
  flushRef.current = session.flush;

  // Caret target for the session being entered by gesture; cleared when the
  // session ends so a toolbar-pencil session never inherits a stale point.
  const [editFocusPoint, setEditFocusPoint] = useState<BlockEntryPoint | null>(
    null,
  );
  useEffect(() => {
    if (!editing) setEditFocusPoint(null);
  }, [editing]);

  const canEnter = (canEnterEditIslands || canInteract) && focus.mode === null;

  const enter = useCallback(
    (point?: BlockEntryPoint) => {
      // Edit is the superset mode (pointer live + islands editable), so it
      // wins when available; interact covers viewers and island-less blocks.
      if (canEnterEditIslands) {
        setEditFocusPoint(point ?? null);
        focus.enterEdit();
        return;
      }
      if (canInteract) focus.enterInteract();
    },
    [canEnterEditIslands, canInteract, focus],
  );

  // One-shot deferred entry for the toolbar (see the interface comment).
  const [enterRequested, setEnterRequested] = useState(false);
  const requestEnter = useCallback(() => setEnterRequested(true), []);
  useEffect(() => {
    if (!enterRequested) return;
    // Deselection wins over capability — the two can land in the SAME render@
    // (the persisted flag arrives after a click-away), and entering would
    // steal selection back via enter()'s addSelectedNodes.
    if (!selected) {
      setEnterRequested(false);
      return;
    }
    if (canEnterEditIslands || canInteract) {
      setEnterRequested(false);
      enter();
    }
  }, [enterRequested, canEnterEditIslands, canInteract, enter, selected]);

  // Keyboard drill-in (Figma's Enter): enter the selected block. Window-level
  // because React Flow nodes don't reliably hold DOM focus; guarded so typing
  // contexts and multi-selection never trigger it.
  const canEnterRef = useRef(canEnter);
  canEnterRef.current = canEnter;
  const enterRef = useRef(enter);
  enterRef.current = enter;
  useEffect(() => {
    if (!selected || focus.mode !== null) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter") return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
      }
      if (!canEnterRef.current) return;
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      // Only when this block is the single selection — Enter over a marquee
      // of nodes must not race every selected block for the latch.
      const nodes = storeApi.getState().nodes;
      let selectedCount = 0;
      for (const node of nodes) {
        if (node.selected && ++selectedCount > 1) break;
      }
      if (selectedCount !== 1) return;
      event.preventDefault();
      enterRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selected, focus.mode, storeApi]);

  const toggleEditSession = useCallback(() => {
    if (editing) focus.exit();
    else focus.enterEdit();
  }, [editing, focus]);

  const onBlockMessage = useCallback(
    (msg: BlockToParentMessage) => {
      // Escape inside the iframe: the parent's key listener can't see it,
      // because the activated block holds focus in another document.
      if (msg.type === "exit-request") {
        focus.exit();
        return;
      }
      session.onIslandMessage(msg);
    },
    [focus, session],
  );

  const iframeOwnsPointer =
    focus.mode !== null && !dragging && cursorTool !== "hand";

  // Announce what the gesture will do BEFORE it happens — this label is what
  // turns the per-block routing from a trap into a labeled affordance.
  const enterHintLabel =
    canEnterEditIslands && canInteract
      ? "Double-click to interact · text editable"
      : canEnterEditIslands
        ? "Double-click to edit text"
        : canInteract
          ? "Double-click to interact"
          : "Double-click to open";

  const islandHint = canEnterEditIslands && focus.mode === null;

  return useMemo(
    () => ({
      presentation,
      theme,
      transformHtml: presign.transformHtml,
      focusMode: focus.mode,
      canEnter,
      enter,
      requestEnter,
      enterHintLabel,
      hasIslands,
      islandHint,
      editFocusPoint,
      onRuntimeReady,
      interactiveFlag,
      iframeOwnsPointer,
      onBlockMessage,
      exit: focus.exit,
      islandsEditable: editing,
      toggleEditSession,
      contentFrozen: session.contentFrozen,
      saveState: session.saveState,
    }),
    [
      presentation,
      theme,
      presign.transformHtml,
      focus.mode,
      focus.exit,
      canEnter,
      enter,
      requestEnter,
      enterHintLabel,
      hasIslands,
      islandHint,
      editFocusPoint,
      onRuntimeReady,
      interactiveFlag,
      iframeOwnsPointer,
      onBlockMessage,
      editing,
      toggleEditSession,
      session.contentFrozen,
      session.saveState,
    ],
  );
}
